import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEO from '@/components/feature/SEO';
import { articles, categories } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const categoryArticles = articles.filter((a) => a.category === slug);

  const formatViews = (v: number) => {
    if (v >= 10000) return (v / 10000).toFixed(1) + '万';
    return v.toString();
  };

  if (!category) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center bg-background-50">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground-950 mb-4">分类不存在</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'CollectionPage',
        name: `${category.name} - 吃瓜视频网`,
        url: `http://51fuliji.cn/category/${slug}`,
        description: `${category.description}，共 ${category.count.toLocaleString()} 篇内容。`,
        isPartOf: { '@id': 'http://51fuliji.cn/#website' },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: '首页',
            item: 'http://51fuliji.cn/',
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: category.name,
            item: `http://51fuliji.cn/category/${slug}`,
          },
        ],
      },
    ],
  };

  return (
    <>
      <SEO
        title={`${category.name} - 吃瓜视频网 | 吃瓜视频在线播放`}
        description={`吃瓜视频网${category.name}频道，${category.description}。共${category.count.toLocaleString()}篇内容，每日更新最热${category.name}视频，吃瓜视频在线播放首选平台。`}
        keywords={`${category.name},吃瓜视频,吃瓜视频在线播放,在线观看吃瓜视频,国产吃瓜视频,吃瓜视频网站,${category.name}视频`}
        canonicalUrl={`http://51fuliji.cn/category/${slug}`}
        ogTitle={`${category.name} - 吃瓜视频网`}
        ogDescription={category.description}
        ogUrl={`http://51fuliji.cn/category/${slug}`}
        ogType="website"
        jsonLd={jsonLd}
      />

      <Navbar />

      <main className="pt-16 bg-background-50">
        <section className="w-full bg-background-50">
          <div className="w-full px-4 md:px-6 pt-12 pb-8 md:pt-16 md:pb-10">
            <div className="mb-6">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: category.name },
                ]}
              />
            </div>
            <div className="flex flex-col lg:flex-row lg:items-start gap-6 lg:gap-12">
              <div className="lg:w-1/2">
                <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-[1.1]">
                  {category.name}
                </h1>
              </div>
              <div className="lg:w-1/2 lg:pt-2">
                <p className="text-base text-foreground-600 leading-relaxed mb-4 max-w-md">
                  {category.description} — 共 {category.count.toLocaleString()} 篇内容
                </p>
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="px-4 py-1.5 bg-primary-500 text-background-50 text-xs font-medium rounded-full whitespace-nowrap">最新</span>
                  <span className="px-4 py-1.5 bg-background-100 text-foreground-600 text-xs font-medium rounded-full hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap">最热</span>
                  <span className="px-4 py-1.5 bg-background-100 text-foreground-600 text-xs font-medium rounded-full hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap">视频</span>
                  <span className="px-4 py-1.5 bg-background-100 text-foreground-600 text-xs font-medium rounded-full hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap">图文</span>
                </div>
              </div>
            </div>

            <div className="mt-8 relative overflow-hidden rounded-2xl h-[200px] md:h-[300px]">
              <div className="w-full h-full bg-gradient-to-br from-background-100 via-background-50 to-primary-500/10 flex items-center justify-center">
                <div className="text-center">
                  <i className={`${category.icon} text-5xl md:text-7xl text-primary-500/40`}></i>
                  <p className="mt-3 text-foreground-500 text-sm">{category.name} · 精彩内容尽在掌握</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-950 py-12 md:py-16">
          <div className="w-full px-4 md:px-6">
            <div className="mb-8">
              <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-50">
                全部内容
              </h2>
              <p className="text-sm text-foreground-400 mt-2">
                共 {categoryArticles.length} 篇
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {categoryArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="group block bg-background-50 rounded-xl overflow-hidden hover:ring-2 hover:ring-primary-500/50 transition-all duration-300"
                >
                  <div className="relative h-48 md:h-56 overflow-hidden">
                    <img
                      src={article.imageUrl}
                      alt={article.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      title={article.title}
                    />
                    <div className="absolute top-3 left-3 flex gap-2">
                      {article.isNew && (
                        <span className="px-2 py-0.5 bg-primary-500 text-background-50 text-xs font-medium rounded-full whitespace-nowrap">新</span>
                      )}
                      <span className="px-2 py-0.5 bg-background-50/80 backdrop-blur-sm text-foreground-950 text-xs font-medium rounded-full whitespace-nowrap">
                        {article.categoryName}
                      </span>
                    </div>
                    <div className="absolute bottom-3 right-3 bg-background-50/80 backdrop-blur-sm rounded-full px-3 py-1 flex items-center gap-1">
                      <i className="ri-play-circle-fill text-primary-500 text-sm"></i>
                      <span className="text-xs text-foreground-950 font-medium">
                        {formatViews(article.views)}
                      </span>
                    </div>
                  </div>
                  <div className="p-5">
                    <h3 className="text-base font-bold text-foreground-950 leading-snug mb-3 line-clamp-2 group-hover:text-primary-500 transition-colors">
                      {article.title}
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2 mb-4">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center justify-between text-xs text-foreground-500">
                      <span>{article.date}</span>
                      <div className="flex items-center gap-3">
                        <span className="flex items-center gap-1">
                          <i className="ri-eye-line"></i>
                          {formatViews(article.views)}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-chat-3-line"></i>
                          {formatViews(article.comments)}
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {categoryArticles.length === 0 && (
              <div className="text-center py-16">
                <i className="ri-inbox-line text-5xl text-foreground-400"></i>
                <p className="mt-4 text-foreground-500">该分类暂无内容</p>
              </div>
            )}
          </div>
        </section>

        <section className="w-full bg-background-900 py-12 md:py-16">
          <div className="w-full px-4 md:px-6">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-50 mb-8">
              其他热门频道
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {categories
                .filter((c) => c.slug !== slug)
                .map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="group flex flex-col items-center p-5 rounded-xl bg-background-50 hover:bg-primary-500 transition-all duration-300"
                  >
                    <i className={`${cat.icon} text-2xl text-primary-500 group-hover:text-background-50 transition-colors duration-300 mb-3`}></i>
                    <span className="text-sm font-medium text-foreground-950 group-hover:text-background-50 transition-colors duration-300 whitespace-nowrap">
                      {cat.name}
                    </span>
                    <span className="text-xs text-foreground-500 group-hover:text-background-50/70 transition-colors duration-300 mt-1">
                      {cat.count.toLocaleString()} 篇
                    </span>
                  </Link>
                ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}